/** 초기 업로드 뷰 — DSD 파일 + (신규 IXD 업로드 | 기존 IXD report 선택) 모드 분기 */

import { NewChip } from "@/components/common/new-chip";
import { Button } from "@/toggle-ui-kit/button";
import { GitCompare, Loader2 } from "lucide-react";
import {
  BetaFeatureSegmentControl,
  type BetaFeatureSegmentControlItem,
} from "@/components/beta-feature";
import { ComparisonHistory } from "./comparison-history";
import { ExistingIxdReportPicker } from "@/components/existing-ixd-report-picker";
import { FileUploadSection } from "@/components/file-upload-section";
import { IxdUploadNotice } from "@/components/ixd-upload-notice";
import { UPLOAD_CARD_BY_MODE } from "@/constants/ixd-upload-mode";
import { FuzzButtonDevMode } from "./fuzz-button";
import type { IxdSourceMode } from "@/hooks/ixd-dsd-compare/use-compare";
import { BetaFeatureOnly } from "../beta-feature";

interface InitialUploadViewProps {
  sourceMode: IxdSourceMode;
  onSourceModeChange: (mode: IxdSourceMode) => void;
  onDsdSelect: (file: File) => void;
  onIxdSelect: (file: File) => void;
  selectedIxdReportId: string | null;
  onIxdReportSelect: (reportId: string | null) => void;
  dsdLoading: boolean;
  ixdLoading: boolean;
  dsdFile: File | null;
  ixdFile: File | null;
  onCompare: () => void;
  canCompare: boolean;
  isComparing: boolean;
  onHistorySelect?: (comparisonId: string) => void;
  onFuzz?: () => void;
  isFuzzing?: boolean;
}

const SOURCE_MODE_ITEMS = [
  { value: "upload", label: "새 IXD 업로드", isBetaFeature: false },
  { value: "existing-report", label: "기존 IXD 선택", isBetaFeature: false },
  { value: "dart-filing", label: "XBRL 공시본 업로드", isBetaFeature: true },
] satisfies BetaFeatureSegmentControlItem<IxdSourceMode>[];

/** 화면마다 뜻이 다른 설명만 여기서 정한다. label/accept 는 공유 상수. */
const CARD_DESCRIPTION_BY_MODE = {
  upload: ".ixd 형식",
  "dart-filing": "DART 공시 XBRL(.zip) — 값 편집은 불가",
} as const;

export const InitialUploadView = ({
  sourceMode,
  onSourceModeChange,
  onDsdSelect,
  onIxdSelect,
  selectedIxdReportId,
  onIxdReportSelect,
  dsdLoading,
  ixdLoading,
  dsdFile,
  ixdFile,
  onCompare,
  canCompare,
  isComparing,
  onHistorySelect,
  onFuzz,
  isFuzzing = false,
}: InitialUploadViewProps) => {
  return (
    <div className="flex-1 min-w-0 flex flex-col items-center overflow-auto bg-white py-32">
      <div className="flex flex-col gap-9 items-center w-full p-16 min-w-[784px]">
        {/* 헤더 섹션 */}
        <div className="flex flex-col gap-2 items-start max-w-[1144px] w-full">
          <div className="flex items-center gap-2">
            <h1 className="title-2-bold text-label-normal">DSD - IXD 비교</h1>
            <NewChip color="indigo" size="large" />
          </div>
          <p className="body-1-normal-medium text-label-neutral">
            DSD와 IXD 보고서를 대사하여 값의 일치/불일치 항목을 시각화하세요.
          </p>
        </div>

        {/* 소스 모드 선택 */}
        <div className="flex justify-start max-w-[1144px] w-full">
          <BetaFeatureSegmentControl
            items={SOURCE_MODE_ITEMS}
            value={sourceMode}
            onChange={onSourceModeChange}
            size="small"
          />
        </div>

        {/* 버튼 + 카드 컨테이너 */}
        <div className="flex flex-col gap-5 items-center w-full">
          {/* 버튼 행 */}
          <div className="flex items-center justify-end gap-2 max-w-[1144px] w-full">
            {/* fuzz는 .ixd 업로드에서만 — 기존 report·공시본에는 의미 없다. */}
            {sourceMode === "upload" && (
              <FuzzButtonDevMode
                onFuzz={onFuzz}
                isFuzzing={isFuzzing}
                disabled={!canCompare}
              />
            )}
            <Button
              variant="solid"
              color="secondary"
              size={36}
              leftIcon={
                isComparing ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <GitCompare size={18} />
                )
              }
              disabled={!canCompare || isComparing}
              onClick={onCompare}
            >
              {isComparing ? "비교 분석 중..." : "DSD - IXD 비교"}
            </Button>
          </div>

          {/* 카드 행 */}
          <div className="flex gap-6 items-start justify-center w-full max-w-[1144px]">
            <FileUploadSection
              label="DSD 파일"
              accept=".dsd"
              onFileSelect={onDsdSelect}
              isLoading={dsdLoading}
              fileName={dsdFile?.name}
              description=".dsd 형식"
              accentColor="blue"
            />
            {sourceMode !== "existing-report" ? (
              <FileUploadSection
                label={UPLOAD_CARD_BY_MODE[sourceMode].label}
                accept={UPLOAD_CARD_BY_MODE[sourceMode].accept}
                onFileSelect={onIxdSelect}
                isLoading={ixdLoading}
                fileName={ixdFile?.name}
                description={CARD_DESCRIPTION_BY_MODE[sourceMode]}
                accentColor="purple"
              />
            ) : (
              <ExistingIxdReportPicker
                selectedReportId={selectedIxdReportId}
                onSelect={onIxdReportSelect}
                enabled={sourceMode === "existing-report"}
              />
            )}
          </div>

          <IxdUploadNotice
            accepts={sourceMode === "dart-filing" ? ".dsd, .zip" : ".dsd, .ixd"}
            showConversionGuide={
              sourceMode === "existing-report" ||
              UPLOAD_CARD_BY_MODE[sourceMode].showConversionGuide
            }
            className="w-full max-w-[1144px]"
          />
        </div>

        {/* 비교 이력 — 최근 5개 */}
        {onHistorySelect && (
          <BetaFeatureOnly>
            <div className="max-w-[1144px] w-full">
              <ComparisonHistory onSelect={onHistorySelect} />
            </div>
          </BetaFeatureOnly>
        )}
      </div>
    </div>
  );
};
